import { isValidObjectId } from "../find_unique_ids_in_list";
import { matchesReportStatus } from "./reportStatusOptions";

export interface ReportingHubLookupMaps {
	project: Map<
		string,
		{ operators: Set<string>; wells: Set<string>; operations: Set<string> }
	>;
	well: Map<
		string,
		{ operators: Set<string>; projects: Set<string>; operations: Set<string> }
	>;
	operation: Map<
		string,
		{ operator?: string; project?: string; well?: string }
	>;
}

export interface ReportingHubFilterSelection {
	operators: string[];
	projects: string[];
	wells: string[];
	operations: string[];
}

export interface ReportingHubFilters {
	operators?: string[];
	statuses?: string[];
	projects?: string[];
	wells?: string[];
	operations?: string[];
}

export const buildLookupMaps = (reports: any[]): ReportingHubLookupMaps => {
	const maps: ReportingHubLookupMaps = {
		project: new Map(),
		well: new Map(),
		operation: new Map(),
	};

	for (const report of reports) {
		const operator = report.operator as string | undefined;
		const projectId = report.project as string | undefined;
		const wellId = report.well as string | undefined;
		const operationId = report.operation as string | undefined;

		if (projectId) {
			const entry = maps.project.get(projectId) ?? {
				operators: new Set<string>(),
				wells: new Set<string>(),
				operations: new Set<string>(),
			};
			if (operator) entry.operators.add(operator);
			if (wellId) entry.wells.add(wellId);
			if (operationId) entry.operations.add(operationId);
			maps.project.set(projectId, entry);
		}

		if (wellId) {
			const entry = maps.well.get(wellId) ?? {
				operators: new Set<string>(),
				projects: new Set<string>(),
				operations: new Set<string>(),
			};
			if (operator) entry.operators.add(operator);
			if (projectId) entry.projects.add(projectId);
			if (operationId) entry.operations.add(operationId);
			maps.well.set(wellId, entry);
		}

		if (operationId) {
			const entry = maps.operation.get(operationId) ?? {};
			if (operator && !entry.operator) entry.operator = operator;
			if (projectId && !entry.project) entry.project = projectId;
			if (wellId && !entry.well) entry.well = wellId;
			maps.operation.set(operationId, entry);
		}
	}

	return maps;
};

export const cascadeOperatorChange = (
	nextOperators: string[],
	current: ReportingHubFilterSelection,
	maps: ReportingHubLookupMaps
): ReportingHubFilterSelection => {
	if (nextOperators.length === 0) {
		return { operators: [], projects: [], wells: [], operations: [] };
	}

	const operatorsSet = new Set(nextOperators);

	const nextProjects = current.projects.filter((projectId) => {
		const entry = maps.project.get(projectId);
		return entry && [...entry.operators].some((op) => operatorsSet.has(op));
	});

	const nextWells = current.wells.filter((wellId) => {
		const entry = maps.well.get(wellId);
		return entry && [...entry.operators].some((op) => operatorsSet.has(op));
	});

	const nextOperations = current.operations.filter((operationId) => {
		const entry = maps.operation.get(operationId);
		return entry && entry.operator && operatorsSet.has(entry.operator);
	});

	return {
		operators: nextOperators,
		projects: nextProjects,
		wells: nextWells,
		operations: nextOperations,
	};
};

export const cascadeProjectChange = (
	nextProjects: string[],
	current: ReportingHubFilterSelection,
	maps: ReportingHubLookupMaps
): ReportingHubFilterSelection => {
	const projectsSet = new Set(nextProjects);

	const nextWells = current.wells.filter((wellId) => {
		const entry = maps.well.get(wellId);
		return entry && [...entry.projects].some((p) => projectsSet.has(p));
	});

	const wellsSet = new Set(nextWells);

	const nextOperations = current.operations.filter((operationId) => {
		const entry = maps.operation.get(operationId);
		return (
			entry &&
			entry.project &&
			projectsSet.has(entry.project) &&
			entry.well &&
			wellsSet.has(entry.well)
		);
	});

	const finalOperators = new Set(current.operators);
	for (const projectId of nextProjects) {
		const entry = maps.project.get(projectId);
		if (entry) entry.operators.forEach((op) => finalOperators.add(op));
	}
	for (const wellId of nextWells) {
		const entry = maps.well.get(wellId);
		if (entry) entry.operators.forEach((op) => finalOperators.add(op));
	}
	for (const operationId of nextOperations) {
		const entry = maps.operation.get(operationId);
		if (entry?.operator) finalOperators.add(entry.operator);
	}

	return {
		operators: [...finalOperators],
		projects: nextProjects,
		wells: nextWells,
		operations: nextOperations,
	};
};

export const cascadeWellChange = (
	nextWells: string[],
	current: ReportingHubFilterSelection,
	maps: ReportingHubLookupMaps
): ReportingHubFilterSelection => {
	const wellsSet = new Set(nextWells);

	const nextOperations = current.operations.filter((operationId) => {
		const entry = maps.operation.get(operationId);
		return entry && entry.well && wellsSet.has(entry.well);
	});

	const nextProjects = new Set(current.projects);
	for (const wellId of nextWells) {
		const entry = maps.well.get(wellId);
		if (entry) entry.projects.forEach((p) => nextProjects.add(p));
	}

	const finalOperators = new Set(current.operators);
	for (const projectId of nextProjects) {
		const entry = maps.project.get(projectId);
		if (entry) entry.operators.forEach((op) => finalOperators.add(op));
	}
	for (const wellId of nextWells) {
		const entry = maps.well.get(wellId);
		if (entry) entry.operators.forEach((op) => finalOperators.add(op));
	}
	for (const operationId of nextOperations) {
		const entry = maps.operation.get(operationId);
		if (entry?.operator) finalOperators.add(entry.operator);
	}

	return {
		operators: [...finalOperators],
		projects: [...nextProjects],
		wells: nextWells,
		operations: nextOperations,
	};
};

export const cascadeOperationChange = (
	nextOperations: string[],
	current: ReportingHubFilterSelection,
	maps: ReportingHubLookupMaps
): ReportingHubFilterSelection => {
	const nextWells = new Set(current.wells);
	const nextProjects = new Set(current.projects);
	const nextOperators = new Set(current.operators);

	for (const operationId of nextOperations) {
		const entry = maps.operation.get(operationId);
		if (entry?.well) nextWells.add(entry.well);
		if (entry?.project) nextProjects.add(entry.project);
		if (entry?.operator) nextOperators.add(entry.operator);
	}

	return {
		operators: [...nextOperators],
		projects: [...nextProjects],
		wells: [...nextWells],
		operations: nextOperations,
	};
};

export const applyReportingHubFilters = (
	reports: any[],
	filters: ReportingHubFilters
) => {
	return reports.filter((report) => {
		if (
			filters.operators?.length &&
			!filters.operators.includes(report.operator)
		) {
			return false;
		}

		if (filters.statuses?.length) {
			const itemStatus = report.status?.toLowerCase();
			if (itemStatus === "closed") {
				if (!filters.statuses.includes("closed")) return false;
			} else if (!matchesReportStatus(report.open_status, filters.statuses)) {
				return false;
			}
		}

		if (
			filters.projects?.length &&
			!filters.projects.includes(report.project)
		) {
			return false;
		}

		if (filters.wells?.length && !filters.wells.includes(report.well)) {
			return false;
		}

		if (
			filters.operations?.length &&
			!filters.operations.includes(report.operation)
		) {
			return false;
		}

		return true;
	});
};

export const buildOptions = (
	reports: any[],
	field: string,
	labelMap: Record<string, any>,
	labelKey: string
) => {
	const ids = [
		...new Set(reports.map((report) => report[field]).filter(Boolean)),
	] as string[];

	return ids
		.map((id) => {
			const item = labelMap?.[id];
			return {
				label: item?.[labelKey] ?? id,
				value: id,
			};
		})
		.sort((a, b) => a.label.localeCompare(b.label));
};

// Lookups by id, combined (useQueries' combine): every query's records by
// `_id`, and whether any query is still loading. Within combine, the result
// keeps its identity until a query's records or loading state change.
export const combineIdMaps = (
	results: readonly { data?: unknown; isLoading: boolean }[]
) => {
	const map: Record<string, any> = {};
	for (const result of results) {
		const items = Array.isArray(result.data) ? result.data : [];
		for (const item of items) {
			if (item?._id) map[item._id] = item;
		}
	}
	return { map, isLoading: results.some((result) => result.isLoading) };
};

// The ids one field of the reports holds, by operator, once each and sorted:
// what each tenant's lookup asks for. Only ObjectId text counts. A value that
// can't be an id, such as a legacy lesson's text well, would fail the whole
// `$in` filter. A Map, so a stored operator such as "constructor" is only a
// key.
export const groupObjectIdsByOperator = (
	reports: readonly Record<string, any>[],
	field: string
): Map<string, string[]> => {
	const idsByOperator = new Map<string, Set<string>>();
	for (const report of reports) {
		const { operator } = report;
		const id = report[field];
		if (typeof operator !== "string" || operator === "") continue;
		if (!isValidObjectId(id)) continue;
		const ids = idsByOperator.get(operator) ?? new Set<string>();
		ids.add(id);
		idsByOperator.set(operator, ids);
	}
	return new Map(
		[...idsByOperator].map(([operator, ids]) => [
			operator,
			[...ids].sort((a, b) => a.localeCompare(b)),
		])
	);
};

export const toIdMap = (items: any[] | null | undefined, key = "_id") => {
	const map: Record<string, any> = {};
	if (!items) return map;
	for (const item of items) {
		if (item?.[key]) {
			map[item[key]] = item;
		}
	}
	return map;
};
